import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import FileUpload from "components/FileUpload";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import { UpdateAuthUserData } from "store/reducer";
import { refreshAuthUser } from "pages/settings/settingsHelper";

export default function SettingsChangeImagePage() {
  const dispatch = useDispatch();
  const [uploadItems, setUploadItems] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const lastSavedUrlRef = useRef(null);

  const saveAvatarToProfile = useCallback(
    async (url) => {
      const trimmed = String(url ?? "").trim();
      if (!trimmed) return;

      setSavingProfile(true);
      try {
        const resp = await api.patch(API_ENDPOINTS.profile, { avatarUrl: trimmed });
        if (!isSuccessResponse(resp)) {
          await swtAlert.error(
            "Update failed",
            resp?.message || "Could not save your profile image."
          );
          return;
        }
        await dispatch(UpdateAuthUserData({ avatarUrl: trimmed }));
        await refreshAuthUser(dispatch);
        setUploadItems([]);
        await swtAlert.success(
          "Profile image updated",
          resp?.message || "Your profile image has been saved."
        );
      } catch (err) {
        await swtAlert.error(
          "Update failed",
          err?.message || "Could not save your profile image."
        );
      } finally {
        setSavingProfile(false);
      }
    },
    [dispatch]
  );

  const handleItemsChange = useCallback(
    (items) => {
      setUploadItems(items);
      const uploaded = items.find((item) => item.status === "complete" && item.url);
      if (
        uploaded?.url &&
        uploaded.url !== lastSavedUrlRef.current &&
        !savingProfile
      ) {
        lastSavedUrlRef.current = uploaded.url;
        saveAvatarToProfile(uploaded.url);
      }
    },
    [saveAvatarToProfile, savingProfile]
  );

  return (
    <div className="max-w-xl">
      <h1 className="text-xl font-semibold text-text-900 md:text-2xl">Change image</h1>
      <div className="mt-2 h-0.5 w-full bg-brand-900" aria-hidden />

      <p className="mt-6 text-sm text-text-500">Upload a JPG, PNG, or GIF. Max 5 MB.</p>

      <div className="mt-4">
        <FileUpload
          title="Profile picture"
          accept=".jpg,.jpeg,.png,.gif"
          multiple={false}
          items={uploadItems}
          onItemsChange={handleItemsChange}
          helperText={savingProfile ? "Saving profile image…" : undefined}
        />
      </div>
    </div>
  );
}
